import { useClerk } from "@clerk/tanstack-react-start";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { acceptOrganizationInviteFn } from "~/lib/functions/cco-representatives";
import { requireAuth } from "~/lib/functions/require-auth";
import { createNoIndexMeta } from "~/lib/shared/meta";

/**
 * The link in a representative invite. A signed-out person signs in or
 * creates an account and comes back; the invite is accepted only for the
 * address it was sent to.
 */
export const Route = createFileRoute("/_public/organization-invite/$token")({
  staticData: { title: "Organization invitation" },
  beforeLoad: async ({ location }) => {
    const user = await requireAuth(location);
    return { user };
  },
  loader: async ({ params, context }) => {
    const result = await acceptOrganizationInviteFn({
      data: { token: params.token },
    });
    if (result.status === "accepted") {
      throw redirect({
        to: "/my-organization/$slug",
        params: { slug: result.slug },
      });
    }
    return { result, signedInEmail: context.user.email };
  },
  head: () =>
    createNoIndexMeta(
      "Organization invitation – Dream Coach Match",
      "Accept your invitation to represent an organization on Dream Coach Match.",
    ),
  component: OrganizationInvitePage,
});

function OrganizationInvitePage(): React.JSX.Element {
  const { result, signedInEmail } = Route.useLoaderData();
  const { token } = Route.useParams();
  const { signOut } = useClerk();

  return (
    <div className="mx-auto max-w-xl px-4 py-12 sm:py-16">
      <Card>
        {result.status === "email_mismatch" ? (
          <>
            <h1 className="font-heading text-2xl font-medium tracking-tight text-gray-900">
              This invitation is for another email address
            </h1>
            <p className="text-text-secondary text-sm">
              The invitation to represent {result.organization} was sent to{" "}
              <strong className="font-medium text-gray-900">
                {result.invitedEmail}
              </strong>
              {signedInEmail
                ? `, but you're signed in as ${signedInEmail}`
                : ""}
              . Sign in with {result.invitedEmail} to accept it. Nothing has
              changed on this account.
            </p>
            <div>
              <Button
                variant="primary"
                onClick={() =>
                  void signOut({
                    redirectUrl: `/login?redirect=${encodeURIComponent(`/organization-invite/${token}`)}`,
                  })
                }
              >
                Switch account
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-medium tracking-tight text-gray-900">
              This invitation no longer works
            </h1>
            <p className="text-text-secondary text-sm">
              It has expired or was already used. Reply to your invitation email
              and we'll send you a new one.
            </p>
          </>
        )}
      </Card>
    </div>
  );
}
